import {
  useFavoriteSession,
  useGetBulkEvents,
  useGetTemporalPublicSessions,
  useGetTicketTypesByEventId,
  useUserEventTicket,
} from '@sunrise-ui/api-client';
import type { Event } from '@sunrise-ui/api/events';
import { SessionStatus, TemporalRelation } from '@sunrise-ui/api/events';
import {
  Button,
  ButtonVariants,
  TextReg,
  useAuthStore,
  useDynamicStyles,
  type WithResponsive,
} from '@sunrise-ui/primitives';
import { DateTime } from 'luxon';
import React, { useMemo, useState } from 'react';
import { StyleSheet } from 'react-native';
import { useTicketPurchase } from '../../hooks/useTicketPurchase';

import { TicketReceiptModal } from './TicketReceiptModal';

type Props = {
  communityId?: string;
};

export function CommunityTicketButton({ communityId }: Props) {
  const styles = useDynamicStyles(createStyles);
  const { isAuthenticated } = useAuthStore();
  const [showReceiptModal, setShowReceiptModal] = useState(false);

  // Get community sessions
  const { data: sessions, isLoading: isLoadingSessions } =
    useGetTemporalPublicSessions({
      temporalRelation: TemporalRelation.Future,
      filterStatus: SessionStatus.Confirmed,
      communityIds: [communityId ?? ''],
      enabled: Boolean(communityId),
    });

  // Extract unique event IDs from sessions
  const eventIds = useMemo(() => {
    if (!sessions) return [];

    // Get unique event IDs from all sessions
    return Array.from(
      new Set(sessions.map((session) => session.event_id).filter(Boolean)),
    );
  }, [sessions]);

  // Fetch event details for the sessions
  const { data: events, isLoading: isLoadingEvents } =
    useGetBulkEvents(eventIds);

  // Find the upcoming event that has sessions for this community
  const upcomingEvent = useMemo(() => {
    if (!events || !communityId) {
      return null;
    }

    // Get all events that haven't ended yet (tickets available until end time)
    const now = DateTime.now();
    const futureEvents = events.filter((event: Event) => {
      const eventEndDate = DateTime.fromISO(event.scheduled_end_date || '');
      return eventEndDate > now;
    });

    if (futureEvents.length === 0) {
      return null;
    }

    // Sort by start_date and get the earliest upcoming event
    const sortedEvents = futureEvents.sort((a: Event, b: Event) => {
      const dateA = DateTime.fromISO(a.start_date || '');
      const dateB = DateTime.fromISO(b.start_date || '');
      return dateA.toMillis() - dateB.toMillis();
    });

    return sortedEvents[0];
  }, [events, communityId]);

  const {
    data: notOnSaleTicketTypes,
    isLoading: isLoadingNotOnSaleTicketTypes,
  } = useGetTicketTypesByEventId({
    eventId: upcomingEvent?.id,
    isOnSale: false,
  });

  const { data: onSaleTicketTypes, isLoading: isLoadingOnSaleTicketTypes } =
    useGetTicketTypesByEventId({
      eventId: upcomingEvent?.id,
      isOnSale: true,
    });

  const isExternallyTicketed = Boolean(upcomingEvent?.ticketing_platform_url);

  // Get the single session ID for auto-favoriting (only for single session events)
  const singleSessionId = useMemo(() => {
    if (!sessions || !upcomingEvent) return null;

    const eventSessions = sessions.filter(
      (session) => session.event_id === upcomingEvent.id,
    );

    // Only return session ID if there's exactly one session in the event
    if (eventSessions.length === 1 && eventSessions[0]?.id) {
      return eventSessions[0].id;
    }

    return null;
  }, [sessions, upcomingEvent]);

  const { mutate: favoriteSession } = useFavoriteSession(
    singleSessionId ?? '',
    upcomingEvent?.id,
  );

  // Handle ticket purchase
  const { executePurchase, isPurchasing, isLoadingPurchase } =
    useTicketPurchase({
      eventId: upcomingEvent?.id,
      onSuccess: () => {
        if (singleSessionId) {
          favoriteSession();
        }
      },
    });

  const { userTicket, isLoadingTickets } = useUserEventTicket(
    upcomingEvent?.id,
    upcomingEvent?.community_id,
  );

  const handleButtonPress = async () => {
    // Users with tickets can view receipt (already authenticated)
    if (isAuthenticated && userTicket) {
      setShowReceiptModal(true);
      return;
    }

    // Buy ticket
    await executePurchase();
  };

  const isLoadingTicketTypes =
    isLoadingNotOnSaleTicketTypes || isLoadingOnSaleTicketTypes;
  const hasAnyTicketTypes = Boolean(
    notOnSaleTicketTypes?.length || onSaleTicketTypes?.length,
  );

  // Don't show button if still loading or no upcoming event for this community
  if (
    isLoadingSessions ||
    isLoadingTicketTypes ||
    isLoadingEvents ||
    isLoadingTickets ||
    isLoadingPurchase
  ) {
    return (
      <Button
        loading={true}
        size="small"
        disabled={true}
        labelStyle={styles.loadingButtonLabel}
      >
        {''}
      </Button>
    );
  }

  if (!upcomingEvent) return null;

  // For authenticated users who have tickets, show "View My Ticket"
  if (isAuthenticated && userTicket) {
    return (
      <>
        <Button
          size="small"
          variant={ButtonVariants.WHITE}
          onPress={handleButtonPress}
          style={styles.button}
        >
          <TextReg>View My Ticket</TextReg>
        </Button>

        <TicketReceiptModal
          visible={showReceiptModal}
          onDismiss={() => setShowReceiptModal(false)}
          ticket={userTicket}
          event={upcomingEvent}
        />
      </>
    );
  }

  // Don't show button if no ticket types exist at all (and not externally ticketed)
  if (!hasAnyTicketTypes && !isExternallyTicketed) {
    return null;
  }

  return (
    <Button
      size="small"
      variant={ButtonVariants.PURPLE}
      onPress={handleButtonPress}
      style={styles.button}
      loading={isPurchasing}
      disabled={isPurchasing}
      hideChildrenOnLoading={true}
    >
      <TextReg>Get Tickets</TextReg>
    </Button>
  );
}

const createStyles = ({ isMobile }: WithResponsive) =>
  StyleSheet.create({
    button: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      marginBottom: isMobile ? 0 : 5, // Center vertically on desktop only
    },
    icon: {
      marginRight: 0, // Gap is handled by the button container
    },
    loadingButtonLabel: {
      marginLeft: 16,
      marginRight: 16,
    },
  });
